import { useState, type ReactNode } from 'react';
import { IconAlertCircle, IconAlertTriangle, IconAnchor, IconArrowRight, IconBell, IconBox, IconCheck, IconChevronDown, IconExternalLink, IconList, IconLock, IconNote, IconSettings, IconActivity, IconEye, IconMail, IconCopy, IconFileUpload, IconMessageCircle, IconMessage2, IconNotes, IconRefresh, IconHistory, IconInfoCircle, IconPencil, IconProgress, IconSend, IconShip, IconTimeline, IconTargetArrow, IconFileText, IconArrowBackUp, IconFilter, IconLink, IconPaperclip, IconX, IconArrowsExchange, IconDots, IconTrash } from '@tabler/icons-react';
import { useToast, AppIcon } from '@hana/ui';
import { transitDays, type JobDetail } from './detail';
import { ACTIVITY, ACT_FILTERS, ALERTS, INFO_ROWS, INFO_SUM, MAILS, MAIL_FILTERS, MAIL_SUGGEST, MEMOS, PLAN_STEPS, TASKS, REFS, CONT_ROWS, CUS_ROWS, PIC_ROWS, HEI_MSGS, CLOSE_LIST } from './overviewData';
import { t } from '@hana/i18n';
import './jobDetailI18n';
import './jobListI18n';
import './overviewI18n';
import s from './jobDetail.module.css';

// Tổng quan theo Mint-JobDetail (JD-OV2): trái = kế hoạch · luồng mail · hoạt động · 3 thẻ gập; phải 380 = memo · cảnh báo · việc · thông tin.
// Chỉ đọc: nút ghi báo toast. Không có tiền.

type Tone = 'mute' | 'warn' | 'err' | 'pri' | 'ok';
function RefGroups({ copy, no }: { copy: (label: string, v: string) => void; no: () => void }) {
  const [openK, setOpenK] = useState<Record<string, boolean>>(() => Object.fromEntries(REFS.map((r) => [r.key, r.open])));
  return (
    <>
      {REFS.map((r, i) => (
        <div key={r.key} className={i ? s.rowLine : ''}>
          <button type="button" className={s.grpBtn} aria-expanded={!!openK[r.key]} onClick={() => setOpenK((o) => ({ ...o, [r.key]: !o[r.key] }))}>
            <span className={s.rowGrow} style={{ fontWeight: 500 }}>{r.k}</span>
            <span className={s.rowSm}>{r.rows.length}</span>
            <IconChevronDown size={16} aria-hidden="true" style={{ transform: openK[r.key] ? 'rotate(180deg)' : undefined }} />
          </button>
          {openK[r.key] && (
            <div style={{ paddingBottom: 8 }}>
              {r.rows.map((x) => (
                <div key={x.k} className={s.rowL} style={{ minHeight: 30 }}>
                  <span className={s.rowSm} style={{ width: 108, flexShrink: 0 }}>{x.k}</span>
                  {x.href ? (
                    <a className={s.infoLink} href="/ezy/crm" onClick={(e) => { e.preventDefault(); no(); }}>{x.v}</a>
                  ) : (
                    <span className={s.infoV} style={{ fontSize: 'inherit' }}>{x.v}</span>
                  )}
                  {x.copy && (
                    <button type="button" className={s.copyBtn} aria-label={`Chép ${x.k} ${x.v}`} title={t('ov.copy')} onClick={() => copy(x.copy!, x.v)}>
                      <IconCopy size={14} aria-hidden="true" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </>
  );
}
function Sec({ ord, chipsBody, title, icon, info, chip, chips, open: o = true, fold = true, tools, label, flat = false, children }: { title: string; icon?: ReactNode; info?: string; chip?: { n: ReactNode; tone?: Tone }; chips?: { n: ReactNode; tone?: Tone }[]; fold?: boolean; flat?: boolean; open?: boolean; tools?: ReactNode; label?: string; ord?: number; chipsBody?: boolean; children?: ReactNode }) {
  const [open, setOpen] = useState(o);
  return (
    <section className={s.sec} style={ord ? { order: ord } : undefined} aria-label={label ?? title}>
      <div className={s.secHead}>
        {icon && <span className={s.ic}>{icon}</span>}
        <h2>{title}</h2>
        {info && (
          <span role="img" className={s.lb} aria-label={`Giải thích: ${info}`} title={info}>
            <IconInfoCircle size={14} aria-hidden="true" />
          </span>
        )}
        {!chipsBody && [chip, ...(chips ?? [])].map((c, i) => c && <span key={i} className={`${s.cnt} ${c.tone ? s[`cnt_${c.tone}`] : ''}`}>{c.n}</span>)}
        <span className={s.secTools}>
          {tools}
          {fold && (
            <button type="button" className={s.fold} aria-expanded={open} aria-label={`Mở hoặc gập ${title}`} title={t('ov.toggle')} onClick={() => setOpen((v) => !v)}>
              <IconChevronDown size={16} aria-hidden="true" style={{ transform: open ? 'rotate(180deg)' : undefined }} />
            </button>
          )}
        </span>
      </div>
      {open && children && (
        <div className={`${s.secBody} ${flat ? s.secFlat : ''}`}>
          {chipsBody && <div className={s.chipRow}>{[chip, ...(chips ?? [])].map((c, i) => c && <span key={i} className={`${s.cnt} ${c.tone ? s[`cnt_${c.tone}`] : ''}`}>{c.n}</span>)}</div>}
          {children}
        </div>
      )}
    </section>
  );
}

/** Avatar nhân viên (HanaAvatar của board): có ảnh → hình người, chưa có → chữ cái tắt. */
const HAS_PHOTO: Record<string, boolean> = { DAVID: true, MAITTN: true, PHUONGNKT: true, LIENNTK: true, ThuyNTT: true };
export function Av({ user, size = 18, ext = false }: { user: string; size?: number; ext?: boolean }) {
  const photo = !ext && HAS_PHOTO[user];
  const w = user.replace(/[^\p{L}\p{N} ]/gu, ' ').trim().split(/\s+/);
  const ini = ext && w.length > 1 ? (w[0]![0]! + w[w.length - 1]![0]!) : user.slice(0, 2);
  return (
    <span className={`${s.av} ${photo ? s.avPhoto : ''}`} style={{ width: size, height: size }} title={user} aria-hidden="true">
      {photo ? (
        <svg width="100%" height="100%" viewBox="0 0 40 40" aria-hidden="true">
          <circle cx="20" cy="16" r="7.5" className={s.avSkin} />
          <path d="M5 41c1.2-9.5 7.4-13.5 15-13.5S33.8 31.5 35 41z" className={s.avShirt} />
        </svg>
      ) : (
        ini.toUpperCase()
      )}
    </span>
  );
}


const ACT_IC = { mail: IconMail, chat: IconMessageCircle, st: IconRefresh, doc: IconFileUpload, zns: IconMessage2 } as const;
const mailSt = (k: 'ok' | 'wait' | 'over') => t(k === 'ok' ? 'ov.mailDone' : k === 'wait' ? 'ov.mailWait' : 'ov.overSla');
const ALERT_IC: Record<string, typeof IconBell> = { 'Nhắc điều độ': IconBell, 'Xem cont': IconBox, 'Nhắc khách': IconBell, 'Mở việc': IconExternalLink };
const TASK_IC: Record<string, typeof IconBell> = { Nhắc: IconBell, 'Đã nhận': IconCheck, Khoá: IconLock };

const FOCUS = { k: 'Draft B/L', st: 'Trễ hạn', line: "Draft v2 chờ khách duyệt · quá hạn 1h40'", act: 'Gửi lại Draft' };

function PlanCard({ d, no, ord }: { d: JobDetail; no: () => void; ord?: number }) {
  const [why, setWhy] = useState(false);
  const done = PLAN_STEPS.filter((p) => p.state === 'Xong').length;
  const days = transitDays(d);
  const fmt = (iso: string) => {
    const p = Object.fromEntries(new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Ho_Chi_Minh', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(new Date(iso)).map((x) => [x.type, x.value]));
    return `${p.day}/${p.month}/${p.year} ${p.hour}:${p.minute}`;
  };
  return (
    <section className={s.plan} style={ord ? { order: ord } : undefined} aria-label={t('ov.plan')}>
      <div className={s.planHead}>
        <IconShip size={18} aria-hidden="true" className={s.ic} />
        <h2>{t('jobDetail.planTitle')}</h2>
        <span role="img" className={s.lb} aria-label={t('overview.a11y1')} title={t('overview.a11y2')}>
          <IconInfoCircle size={14} aria-hidden="true" />
        </span>
        <span className={s.planN}>
          {done}/{PLAN_STEPS.length} mốc nội bộ
        </span>
      </div>
      <div className={s.planTop}>
        <div className={s.planMain}>
          <div className={s.planLine}>
            <span role="img" aria-label={t('ov.status')} title={t('ov.status')} className={s.lb}>
              <IconProgress size={16} aria-hidden="true" />
            </span>
            <span className={s.stChip}>
              <span className={s.stDot} />
              {d.status}
            </span>
          </div>
          <div className={s.planLine}>
            <span role="img" aria-label={t('ov.latest')} title={t('ov.latest')} className={s.lb}>
              <IconHistory size={16} aria-hidden="true" />
            </span>
            <span>{t("ovx.gateIn")}</span>
          </div>
          <div className={s.planLine}>
            <span role="img" aria-label={t('ov.next')} title={t('ov.next')} className={s.lb}>
              <IconArrowRight size={16} aria-hidden="true" />
            </span>
            <span>{t("ovx.cyCutoff")}</span>
            <button type="button" className={s.act} onClick={no}>
              <IconBell size={14} aria-hidden="true" />
              {t('jobDetail.ovRemind')}
            </button>
          </div>
          <div className={s.planBtns}>
            <button type="button" className={s.btnPri} onClick={no}>
              <IconTargetArrow size={16} aria-hidden="true" />
              {t('jobDetail.ovFollow')}
            </button>
            <button type="button" className={s.btnCtl} onClick={no}>
              <IconTimeline size={16} aria-hidden="true" />
              {t('jobDetail.ovPlan')}
            </button>
          </div>
        </div>
        <div className={s.transit} title={t('overview.a11y3')}>
          <b>Transit</b>
          <span className={s.transitV}>{t('jobDetail.days', { n: days })}</span>
          <span className={s.transitS}>
            <IconAnchor size={14} aria-hidden="true" />
            {t('jobDetail.ovPortToPort')}
          </span>
        </div>
      </div>
      <div className={s.hr} />
      <div className={s.ends}>
        <div className={s.end}>
          <div className={s.endLab}>POL</div>
          <div className={s.endRow}>
            <span className={s.endIc} aria-hidden="true">
              <IconShip size={18} />
            </span>
            <span className={s.endLine}>
              <button type="button" className={s.vlink} data-vlink={`${d.vessel}|${d.voyage}`} title={t('ov.vesselProfile')} onClick={no}>
                {d.vessel} · {d.voyage}
              </button>
            </span>
          </div>
          <div className={s.port}>
            <span className={s.lab}>{d.pol.code}</span>{t("ovx.polAddr")}
          </div>
          <div className={s.date}>
            <b>{fmt(d.etd)}</b> <span>(ETD)</span>
          </div>
        </div>
        <div className={s.end}>
          <div className={s.endLab}>POD</div>
          <div className={s.endRow}>
            <span className={s.endIc} aria-hidden="true">
              <IconShip size={18} />
            </span>
          </div>
          <div className={s.port}>
            <span className={s.lab}>{d.pod.code}</span>Los Angeles, CA, US
          </div>
          <div className={s.date}>
            <b>{fmt(d.eta)}</b> <span>(ETA)</span>
          </div>
        </div>
      </div>
      <div className={s.chgKey}>
        <span className={s.chgSw} aria-hidden="true" />
        {t('jobDetail.planChange')}
      </div>
      <div className={s.miles} role="list" aria-label={t('ov.milestones')}>
        {PLAN_STEPS.map((p) => (
          <button key={p.label} role="listitem" type="button" className={`${s.mile} ${p.state === 'Trễ hạn' ? s.mileLate : ''}`} aria-label={[p.label, p.state, p.at ?? p.note].filter(Boolean).join(', ')} onClick={no}>
            <span className={`${s.mDot} ${p.state === 'Xong' ? s.mDone : p.state === 'Trễ hạn' ? s.mBad : ''}`} aria-hidden="true">
              {p.state === 'Xong' ? <IconCheck size={12} /> : p.state === 'Trễ hạn' ? <IconAlertCircle size={12} /> : null}
            </span>
            <span className={s.mK}>{p.label}</span>
            <span className={s.mD}>{p.at ?? p.note}</span>
          </button>
        ))}
      </div>
      <div className={s.focus}>
        <span className={s.focusChip}>
          {FOCUS.k} · {FOCUS.st}
        </span>
        <span className={s.focusLine}>{FOCUS.line}</span>
        <span role="img" className={s.lb} aria-label={t('overview.a11y4')} title={t('overview.a11y5')}>
          <IconInfoCircle size={15} aria-hidden="true" />
        </span>
        <button type="button" className={s.act} onClick={no}>
          <IconSend size={16} aria-hidden="true" />
          {FOCUS.act}
        </button>
      </div>
      <div className={s.why}>
        <span>{t('jobDetail.planReason')}</span>
        <button type="button" className={s.whyBtn} aria-expanded={why} aria-label={t('ov.togglePlanWhy')} onClick={() => setWhy((v) => !v)}>
          <IconChevronDown size={18} aria-hidden="true" style={{ transform: why ? 'rotate(180deg)' : undefined }} />
        </button>
      </div>
    </section>
  );
}

export function OverviewBoard({ d, phone = false }: { d: JobDetail; phone?: boolean }) {
  const toast = useToast();
  const ph = (n: number) => (phone ? n : undefined);
  const no = () => toast({ msg: t('jobList.noWrite') });
  const [mailF, setMailF] = useState<string>('all');
  const [actF, setActF] = useState<string>('all');
  const acts = ACTIVITY.filter((a) => actF === 'all' || a.kind === actF);
  const mails = MAILS.filter((m) => mailF === 'all' || m.party === mailF);
  const copy = (label: string, v: string) => {
    void navigator.clipboard?.writeText(v);
    toast({ msg: t('ov.copied', { label }) });
  };
  return (
    <div className={`${s.ov} ${phone ? s.ovPh : ''}`}>
      <div className={s.ovCol}>
        <PlanCard d={d} no={no} ord={ph(3)} />

        <Sec ord={ph(9)} chipsBody={phone}
          title={t('ov.mailFlow')}
          icon={<IconMail size={18} stroke={1.9} aria-hidden="true" />}
          info="Tự gom thư theo JobID, Booking, HBL, MBL từ info@ · support@ · trucking@ · billing@ và hộp của tôi (HANA Box). SLA trả lời 30 phút làm việc. Ask HANA tự gắn thư khớp cả số cont và số booking. Xem toàn màn: Mở trong HANA Box."
          chip={{ n: t('ov.nThreads', { n: MAILS.length }) }}
          chips={[
            { n: t('ov.nUnread', { n: MAILS.filter((m) => m.unread).length }), tone: 'pri' },
            { n: t('ov.nOverSla', { n: MAILS.filter((m) => m.st === 'over').length }), tone: 'err' },
            { n: t('ov.nAuto', { n: MAILS.filter((m) => m.auto).length }), tone: 'pri' },
          ]}
          tools={
            <button type="button" className={s.inkBtn} aria-haspopup="menu" onClick={no}>
              <IconPencil size={14} aria-hidden="true" />
              {t('jobDetail.ovCompose')}
            </button>
          }
        >
          {phone && (
            <a className={s.phAct} href="/hei/" target="_blank" rel="noopener" title={t('overview.a11y9')}>
              <IconMessageCircle size={15} aria-hidden="true" />
              {t('jobDetail.ovAskTeam')}
            </a>
          )}
          <div className={s.mailBar}>
            <div role="radiogroup" aria-label={t('ov.filterParty')} className={s.segBox}>
              {MAIL_FILTERS.map((f) => (
                <button key={f.key} type="button" role="radio" aria-checked={mailF === f.key} className={`${s.segBtn} ${mailF === f.key ? s.segOn : ''}`} onClick={() => setMailF(f.key)}>
                  {f.label} <span className={s.segN}>{f.n}</span>
                </button>
              ))}
            </div>
            <button type="button" className={s.fltBtn} aria-haspopup="menu" onClick={no}>
              <IconFilter size={14} aria-hidden="true" />
              {t('jobDetail.ovTypeBox')}
            </button>
          </div>
          <div className={s.list}>
            {mails.map((m) => (
              <div key={m.subj} className={s.mRow}>
                <button type="button" className={s.mBtn} aria-label={`${m.from} · ${m.subj} · ${mailSt(m.st)}${m.unread ? ` · ${t('ov.unread')}` : ''}`} onClick={no}>
                  <span className={s.mUn} style={{ opacity: m.unread ? 1 : 0 }} aria-hidden="true" />
                  <Av user={m.from} size={28} ext={m.ext} />
                  <span className={s.mFrom} style={{ fontWeight: m.unread ? 600 : 500 }}>
                    {m.from}
                  </span>
                  <span className={s.ell}>
                    <span style={{ fontWeight: m.unread ? 600 : 500 }}>{m.subj}</span>
                    <span className={s.mSnip}> — {m.snip}</span>
                  </span>
                  <span className={s.mKind}>{m.kind}</span>
                  {m.att > 0 && (
                    <span className={s.mAtt} title={t('ov.nAtt', { n: m.att })}>
                      <IconPaperclip size={13} aria-hidden="true" />
                      {m.att}
                    </span>
                  )}
                  {m.n > 1 && (
                    <span className={s.mN} title={t('ov.nInThread', { n: m.n })}>
                      {m.n}
                    </span>
                  )}
                  <span className={s.mSla}>
                    {m.st === 'over' ? (
                      <span className={s.mOver}>{t('ov.overSla')} {m.clock}</span>
                    ) : (
                      <>
                        <span role="img" aria-label={mailSt(m.st)} className={`${s.mSt} ${m.st === 'ok' ? s.mStOk : ''}`} />
                        {m.st === 'wait' && m.clock && <span className={s.mClock}>{m.clock}</span>}
                      </>
                    )}
                  </span>
                  <span className={s.mWhen}>{m.when}</span>
                </button>
                {m.auto && (
                  <span className={s.mAuto}>
                    <span className={s.autoChip} title={m.autoWhy}>
                      <AppIcon app="ask" size={14} />
                      {t('jobDetail.ovAutoTag')}
                    </span>
                    <button type="button" className={s.undoBtn} aria-label={`Hoàn tác tự gắn thư ${m.subj}`} onClick={no}>
                      <IconArrowBackUp size={14} aria-hidden="true" />
                      {t('jobDetail.ovUndo')}
                    </button>
                  </span>
                )}
              </div>
            ))}
          </div>
          <div className={s.sug}>
            <div className={s.sugHead}>
              <AppIcon app="ask" size={16} />
              <b>{t('ov.maybeRelated', { n: MAIL_SUGGEST.length })}</b>
              <span role="img" className={s.lb} aria-label={t('overview.a11y6')} title={t('overview.a11y7')}>
                <IconInfoCircle size={14} aria-hidden="true" />
              </span>
            </div>
            {MAIL_SUGGEST.map((sg) => (
              <div key={sg.subj} className={s.sugRow}>
                <Av user={sg.from} size={24} ext />
                <button type="button" className={s.sugTx} title={t('ov.viewMail')} onClick={no}>
                  <b>{sg.from}</b> · {sg.subj}
                </button>
                <span className={s.sugWhy}>{sg.why}</span>
                <button type="button" className={s.sugLink} onClick={no}>
                  <IconLink size={14} aria-hidden="true" />
                  {t("ovx.bind")}
                </button>
                <button type="button" className={s.sugSkip} onClick={no}>
                  <IconX size={14} aria-hidden="true" />
                  {t('jobDetail.ovSkip')}
                </button>
              </div>
            ))}
          </div>
        </Sec>

        <Sec ord={ph(10)}
          title={t('ov.recentActivity')}
          icon={<IconActivity size={18} stroke={1.9} aria-hidden="true" />}
          info="Gộp mail, chat nhóm job (HANA Hei), đổi trạng thái, chứng từ tải lên và ZNS đã gửi khách (TblJobEventLog + TblEntityInteraction)."
        >
          <div role="radiogroup" aria-label={t('ov.filterActivity')} className={`${s.segBox2}`}>
            {ACT_FILTERS.map((f) => (
              <button key={f.key} type="button" role="radio" aria-checked={actF === f.key} className={`${s.segBtn2} ${actF === f.key ? s.segOn2 : ''}`} onClick={() => setActF(f.key)}>
                {f.label}
              </button>
            ))}
          </div>
          <div className={s.list}>
            {acts.slice(0, 7).map((a) => {
              const Ic = ACT_IC[a.kind];
              return (
                <div key={a.when + a.k} className={s.actRow}>
                  <span className={`${s.actIc} ${s[`act_${a.kind}`]}`} aria-hidden="true">
                    <Ic size={16} />
                  </span>
                  <span className={s.ell}>{a.k}</span>
                  <Av user={a.who} size={22} />
                  <span className={s.actWho}>{a.who}</span>
                  <span className={s.actWhen}>{a.when}</span>
                </div>
              );
            })}
            {!acts.length && <div className={s.actNone}>{t('jobDetail.actNone')}</div>}
          </div>
          <div className={s.actFoot}>
            {acts.length > 7 && (
              <button type="button" className={s.ctlBtn} onClick={no}>
                <IconEye size={14} aria-hidden="true" />
                {t('ov.seeMore', { n: acts.length - 7 })}
              </button>
            )}
            {!phone && (
            <button type="button" className={`${s.linkBtn} ${s.pushR}`} onClick={no}>
                <IconNotes size={14} aria-hidden="true" />
                {t('jobDetail.ovNotes')}
              </button>
            )}
          </div>
        </Sec>

        <Sec ord={ph(11)} title={t('ov.bookingRefs')} flat open={false} icon={<IconFileText size={18} stroke={1.9} aria-hidden="true" />} info="Booking, tham chiếu lô, các bên, lịch cut-off. Mã có nút chép (số đẳng chiều).">
          <RefGroups copy={copy} no={no} />
        </Sec>
        {phone ? (
          <section className={s.sec} style={{ order: 12 }} aria-label="Container">
            <div className={s.phSub}>
              <h2 className={s.phH2}>Container · {CONT_ROWS.length}</h2>
              <button type="button" className={s.phAct} onClick={no}>
                <IconPencil size={16} aria-hidden="true" />
                {t("ovx.add")}
              </button>
              <button type="button" className={s.phAct} aria-label="Import container từ Excel" onClick={no}>
                <IconFileUpload size={16} aria-hidden="true" />
                Import
              </button>
            </div>
            {CONT_ROWS.map((c) => (
              <div key={c.no} className={s.phCont}>
                <span className={s.phContTx}>
                  <b>{c.no}</b>
                  <span>{c.ty} · {c.st}</span>
                </span>
                <button type="button" className={s.phAct} aria-label={`Sửa ${c.no}`} onClick={no}>
                  <IconPencil size={16} aria-hidden="true" />
                  {t("ovx.edit")}
                </button>
                <button type="button" className={s.phAct} aria-label={`Xoá ${c.no}`} onClick={no}>
                  <IconTrash size={16} aria-hidden="true" />
                  {t("ovx.del")}
                </button>
              </div>
            ))}
          </section>
        ) : (
        <Sec title="Containers" flat open={false} icon={<IconBox size={18} stroke={1.9} aria-hidden="true" />} info="Số cont kiểm ISO 6346 · trạng thái hạ bãi theo EIR từ HANA Mov." chip={{ n: `${CONT_ROWS.length}×40HC · hạ ${CONT_ROWS.filter((c) => c.st.startsWith('Đã')).length}/${CONT_ROWS.length}` }}>
          {CONT_ROWS.map((c, i) => {
            const ok = c.st.startsWith('Đã');
            return (
              <div key={c.no} className={`${s.rowL} ${i ? s.rowLine : ''}`}>
                <span role="img" aria-label={c.st} title={c.st} className={s.dot9} style={{ background: ok ? 'var(--d-green)' : 'var(--d-amber)' }} />
                <b className={s.rowB}>{c.no}</b>
                <span className={s.rowSm}>{c.ty}</span>
                <span className={`${s.rowEnd} ${ok ? s.okTx : s.warnTx}`}>{c.st}</span>
                <button type="button" className={s.copyBtn} aria-label={`Chép số cont ${c.no}`} title={t('ov.copy')} onClick={() => copy('số cont', c.no.replace(/\s/g, ''))}>
                  <IconCopy size={14} aria-hidden="true" />
                </button>
              </div>
            );
          })}
        </Sec>
        )}
        <Sec ord={ph(13)} title={t('ov.customs')} flat open={false} icon={<IconFilter size={18} stroke={1.9} aria-hidden="true" />} info="Tờ khai xuất, C/O, VED, hun trùng · chi tiết ở tab Dịch vụ." chip={{ n: 'Luồng xanh', tone: 'ok' }}>
          {CUS_ROWS.map((c, i) => (
            <div key={c.k} className={`${s.rowL} ${i ? s.rowLine : ''}`}>
              <span aria-hidden="true" className={s.dot9} style={{ background: c.tone === 'ok' ? 'var(--d-green)' : c.tone === 'warn' ? 'var(--d-amber)' : 'var(--d-blue)' }} />
              <span className={s.rowGrow}>{c.k}</span>
              <span className={`${s.rowSm} ${c.tone === 'warn' ? s.warnTx : ''}`} style={{ textAlign: 'right' }}>{c.v}</span>
            </div>
          ))}
        </Sec>
      </div>

      {phone && (
        <>
          <div className={s.phActs} style={{ order: 4 }}>
            <button type="button" className={s.phAct} onClick={no}>
              <IconPencil size={16} aria-hidden="true" />
              {t("ovx.editJob")}
            </button>
            {['Tracking hãng', 'Chi tiết tàu', 'Lịch tàu tuyến', 'Cập nhật ETA', 'Gia hạn cut-off', 'Khai VED'].map((k) => (
              <button key={k} type="button" className={s.phAct} onClick={no}>
                {k}
              </button>
            ))}
          </div>
          <section className={s.sec} style={{ order: 14 }} aria-label="Theo dõi">
            <div className={s.phSub}>
              <h3>Follow case</h3>
              <button type="button" className={s.phAct} onClick={no}>
                <IconPencil size={16} aria-hidden="true" />
                {t("ovx.create")}
              </button>
            </div>
            <div className={s.phChips}>
              {['Đang theo dõi 1', 'Hẹn book 1', 'Đã xong 1'].map((k) => (
                <button key={k} type="button" className={s.phChip} onClick={no}>
                  {k}
                </button>
              ))}
            </div>
            <div className={s.phSub}>
              <button type="button" className={s.phAct} onClick={no}>
                {t("ovx.logProg")}
              </button>
              <button type="button" className={s.phAct} onClick={no}>
                Xong
              </button>
            </div>
          </section>
        </>
      )}
      <div className={s.ovCol}>
        <Sec ord={ph(1)}
          title={t('overview.a11y8')}
          icon={<IconNote size={18} stroke={1.9} aria-hidden="true" />}
          info="Memo của khách áp cho mọi job của khách (không phải ghi chú riêng của job): mức Quan trọng tô đỏ, có hạn hiệu lực. Quản lý để thêm memo."
          chip={{ n: MEMOS.length }}
          tools={
            <button type="button" className={s.icBtn} aria-label={t('ov.manage')} title={t('ov.manage')} onClick={no}>
              <IconSettings size={16} aria-hidden="true" />
            </button>
          }
        >
          <div className={s.list}>
            {MEMOS.map((m) => (
              <div key={m.k} className={s.memoRow}>
                <div className={s.memoTop}>
                  <span className={`${s.lv} ${m.lv === 'Quan trọng' ? s.lvHot : ''}`}>{m.lv}</span>
                  <span className={s.memoMeta}>
                    {m.by} · {m.at}
                    {m.exp ? ` · đến ${m.exp}` : ''}
                  </span>
                </div>
                <span>{m.k}</span>
              </div>
            ))}
          </div>
        </Sec>

        <Sec ord={ph(2)}
          title={t('ov.alerts')}
          icon={<IconAlertTriangle size={18} stroke={1.9} aria-hidden="true" />}
          info="Cảnh báo của job (cut-off, free time, chứng từ, việc quá hạn); hiện ở đầu cột phải mọi tab. Nút trên mỗi dòng làm ngay việc cần xử lý."
          chip={{ n: phone ? 3 : ALERTS.length, tone: 'warn' }}
        >
          <div role="region" aria-label={t('ov.jobAlerts')} className={s.list}>
            {ALERTS.slice(0, phone ? 3 : undefined).map((a) => {
              const Ic = phone ? IconEye : (ALERT_IC[a.act] ?? IconBell);
              return (
                <div key={a.text} className={s.alRow}>
                  <span className={`${s.alDot} ${a.tone === 'err' ? s.alErr : ''}`} aria-hidden="true" />
                  <span className={s.ell} title={a.text}>
                    {a.text}
                  </span>
                  <button type="button" className={`${s.icBtn} ${a.tone === 'err' ? s.icErr : s.icWarn}`} aria-label={phone ? 'Xem' : a.act} title={phone ? 'Xem' : a.act} onClick={no}>
                    <Ic size={16} aria-hidden="true" />
                  </button>
                </div>
              );
            })}
          </div>
        </Sec>

        <Sec ord={ph(5)}
          title={t('ov.todo')}
          icon={<IconCheck size={18} stroke={1.9} aria-hidden="true" />}
          info="Việc sinh từ dữ liệu job: thiếu chứng từ, chờ khách duyệt, chờ hãng release. Hạn tính theo giờ làm việc."
          chip={{ n: TASKS.length, tone: 'warn' }}
          tools={
            phone ? undefined : (
              <button type="button" className={s.linkBtn} onClick={no}>
                <IconList size={14} aria-hidden="true" />
                {t('jobDetail.ovAll')}
              </button>
            )
          }
        >
          <div className={s.list}>
            {TASKS.slice(0, 4).map((t) => {
              const Ic = phone ? IconDots : (TASK_IC[t.act] ?? IconBell);
              const act = phone ? `Xử lý: ${t.title}` : t.act;
              return (
                <div key={t.title} className={s.tdRow}>
                  <button type="button" role="checkbox" aria-checked="false" className={s.cb} aria-label={`Xong: ${t.title}`} onClick={no}>
                    <span className={`${s.cbBox} ${t.tone === 'err' ? s.cbErr : ''}`} />
                  </button>
                  <span className={s.tdTx}>
                    <span className={s.tdK}>{t.title}</span>
                    <span className={s.tdSub}>
                      <Av user={t.who} />
                      <span className={s.tdWho}>{t.who} ·</span>
                      <span className={`${s.tdDue} ${t.tone === 'err' ? s.dueErr : t.tone === 'warn' ? s.dueWarn : ''}`}>{t.due}</span>
                    </span>
                  </span>
                  <button type="button" className={`${s.icBtn} ${s.icLine}`} aria-label={act} title={act} onClick={no}>
                    <Ic size={16} aria-hidden="true" />
                  </button>
                </div>
              );
            })}
          </div>
          {TASKS.length > 4 && (
            <button type="button" className={s.more2} onClick={no}>
              <IconEye size={14} aria-hidden="true" />
              {t('ov.seeMore', { n: TASKS.length - 4 })}
            </button>
          )}
        </Sec>

        <Sec ord={ph(6)}
          title={t('ov.mainInfo')}
          icon={<IconFileText size={18} stroke={1.9} aria-hidden="true" />}
          info="Tóm tắt lô (khách → người nhận · tuyến · tàu · ETD / ETA), khách, các bên, hãng, mã booking / B/L. Mã có nút chép."
          tools={
            <button type="button" className={s.icBtn} style={{ borderRadius: 9 }} aria-haspopup="menu" aria-label={t('ov.editRoute')} onClick={no}>
              <IconPencil size={16} aria-hidden="true" />
            </button>
          }
        >
          <div className={s.sum}>
            <div className={s.sumTop}>
              <b>{d.parties[0]?.value}</b>
              <span className={s.sumSep}>→</span>
              <span className={s.sumCnee}>{INFO_SUM.cnee}</span>
            </div>
            <div className={s.sumLine}>
              <span>{INFO_SUM.route}</span>
              <span className={s.sumSep}>·</span>
              <button type="button" className={s.sumVsl} data-vlink={`${d.vessel}|${d.voyage}`} title={t('ov.vesselProfile')} onClick={no}>
                {d.carrier} · {d.vessel} {d.voyage}
              </button>
              <span className={s.sumSep}>·</span>
              <span>
                ETD <b>{INFO_SUM.etd}</b>
              </span>
              <span className={s.sumSep}>·</span>
              <span>ETA {INFO_SUM.eta}</span>
              <span className={s.sumSep}>·</span>
              <span>{INFO_SUM.vol}</span>
            </div>
          </div>
          <div className={s.list}>
            {INFO_ROWS.map((r) => (
              <div key={r.label} className={s.infoRow}>
                <span className={s.infoK}>{r.label}</span>
                {r.link ? (
                  <a className={s.infoLink} href="/ezy/crm" onClick={(e) => { e.preventDefault(); no(); }}>
                    {r.value}
                  </a>
                ) : (
                  <span className={s.infoV}>{r.value}</span>
                )}
                {r.copy && (
                  <button type="button" className={s.copyBtn} aria-label={`Chép ${r.label} ${r.value}`} title={t('ov.copy')} onClick={() => copy(r.label, r.value)}>
                    <IconCopy size={14} aria-hidden="true" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </Sec>

        <Sec ord={ph(7)}
          title={t('ov.pic')}
          flat
          open={false}
          icon={<IconMessageCircle size={18} stroke={1.9} aria-hidden="true" />}
          info="Hai nguồn, gộp và bỏ trùng: (1) theo khách: Sale, CS, Sale Asst, Kế toán ở hồ sơ khách (CUSDATA), job chép sang dòng booking cùng người Add PIC; (2) nhận xử lý / được giao trên job theo TblOrgJD: BKG (BookingTeam), Điều độ. Chưa có người hiện Chưa giao, nút Giao chỉ hiện khi có quyền. Avatar từ TblStaff.ImageProfile. Đổi PIC ở Sửa job."
          tools={
            <button type="button" className={s.tiny} onClick={no}>
              <IconArrowsExchange size={16} aria-hidden="true" />
              {t('jobDetail.ovChangePic')}
            </button>
          }
        >
          {PIC_ROWS.map((u) => (
            <div key={u.user} className={`${s.rowL} ${s.rowLine}`} style={{ minHeight: 52, gap: 12 }}>
              <Av user={u.user} size={32} />
              <span className={s.picCol}>
                <b>{u.user}</b>
                <span>{u.role}</span>
              </span>
            </div>
          ))}
        </Sec>
        <Sec ord={ph(8)} title={t('ov.jobGroup')} flat icon={<IconMessageCircle size={18} stroke={1.9} aria-hidden="true" />} info="Nhóm chat của job trong HANA Hei: tự tạo khi job Active, thành viên đồng bộ Sale / CS / PIC." chip={{ n: '6 thành viên' }}>
          {HEI_MSGS.map((h) => (
            <div key={h.u} className={s.rowL} style={{ minHeight: 30 }}>
              <Av user={h.u} size={22} />
              <span className={s.rowGrow}>
                <b>{h.u}</b> <span className={s.rowSm} style={{ color: 'var(--tx2)', fontSize: 'inherit' }}>{h.k}</span>
              </span>
              <span className={s.rowSm}>{h.when}</span>
            </div>
          ))}
          <div style={{ marginTop: 8 }}>
            <button type="button" className={s.tiny} style={{ height: 32, border: '1px solid var(--bd)', background: 'var(--card)', color: 'var(--tx)', gap: 6, padding: '0 12px', borderRadius: 9 }} title={t('overview.a11y9')} onClick={no}>
              <IconMessageCircle size={15} aria-hidden="true" />
              {t('jobDetail.ovAskTeam')}
            </button>
          </div>
        </Sec>
        {!phone && (
        <Sec title={t('ov.closeJob')} flat open={false} icon={<IconCheck size={18} stroke={1.9} aria-hidden="true" />} info="7 điều kiện đóng job; đủ mới gửi Kế toán khoá." chip={{ n: `${CLOSE_LIST.filter((c) => c.ok).length}/7`, tone: 'warn' }}>
          <div className={s.closeList}>
            {CLOSE_LIST.map((c) => (
              <div key={c.k} className={s.closeRow}>
                <span aria-hidden="true" className={s.closeDot} style={{ background: c.ok ? 'var(--d-green)' : 'var(--bd)' }}>{c.ok ? '✓' : ''}</span>
                <span style={{ color: c.ok ? 'var(--tx)' : 'var(--tx2)' }}>{c.k}</span>
              </div>
            ))}
          </div>
          <button type="button" className={s.closeBtn} disabled title={t('overview.a11y10')}>
            <IconLock size={16} aria-hidden="true" />
            {t('jobDetail.ovReqClose')}
          </button>
        </Sec>
        )}
      </div>
    </div>
  );
}
